import React, { useState, useEffect } from 'react';
import { ExternalLink, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../data/mockData';
import { fetchPortfolio } from '../utils/api';
import { toast } from 'sonner';

const Portfolio = () => {
  const { language } = useLanguage();
  const t = translations[language].portfolio;
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPortfolio();
  }, []);

  const loadPortfolio = async () => {
    try {
      const data = await fetchPortfolio();
      setProjects(data);
    } catch (error) {
      console.error('Error loading portfolio:', error);
      toast.error('Failed to load portfolio');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section id="portfolio" className="py-24 bg-gradient-to-b from-[#0a0c0f] to-[#141619] circuit-pattern">
        <div className="max-w-[1920px] mx-auto px-4 lg:px-8 text-center">
          <div className="text-[#D4AF37] text-xl">Loading portfolio...</div>
        </div>
      </section>
    );
  }

  return (
    <section id="portfolio" className="py-24 bg-gradient-to-b from-[#0a0c0f] to-[#141619] circuit-pattern relative">
      {/* Decorative line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent"></div>
      
      <div className="max-w-[1920px] mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 mb-6 px-4 py-2 bg-gradient-to-r from-[#1a1d23] to-[#141619] rounded-full border border-[#D4AF37]/30">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs font-mono uppercase tracking-widest text-[#D4AF37] font-semibold">
              {language === 'en' ? 'Our Work' : language === 'es' ? 'Nuestro Trabajo' : 'Unsere Arbeit'}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            <span className="text-gold-metallic">{t.title.split(' ')[0]}</span> {t.title.split(' ').slice(1).join(' ')}
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Portfolio Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <div
              key={project.id}
              className="group relative overflow-hidden rounded-2xl transition-all duration-500 hover:scale-[1.02] cursor-pointer"
              style={{
                background: `linear-gradient(135deg, ${project.bgColor}20 0%, ${project.bgColor}40 100%)`,
              }}
            >
              {/* Border glow on hover */}
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#D4AF37]/50 rounded-2xl transition-all duration-500"></div>
              
              {/* Content */}
              <div className="relative p-8 min-h-[320px] flex flex-col justify-between bg-gradient-to-br from-[#1a1d23]/90 to-[#141619]/90 backdrop-blur-sm">
                <div>
                  <div className="inline-block px-4 py-1.5 bg-[#1a1d23]/80 backdrop-blur-sm rounded-full mb-6 border border-[#2a3441]">
                    <span className="text-xs font-mono uppercase tracking-widest text-[#D4AF37] font-semibold">
                      {project.category[language]}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-[#D4AF37] transition-all duration-300">
                    {project.name[language]}
                  </h3>
                  <p className="text-base text-gray-400 leading-relaxed">
                    {project.description[language]}
                  </p>
                </div>

                <div className="flex items-center space-x-2 text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity duration-300 mt-6">
                  <span className="text-sm font-medium">
                    {language === 'en' ? 'View Project' : language === 'es' ? 'Ver Proyecto' : 'Projekt ansehen'}
                  </span>
                  <ExternalLink className="w-4 h-4" />
                </div>
              </div>

              {/* Hover overlay with circuitry effect */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#141619]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none circuit-pattern"></div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Bottom decorative line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#5A6F8F]/30 to-transparent"></div>
    </section>
  );
};

export default Portfolio;
